export interface LocationOption {
    address: string;
    lat: number;
    lng: number;
}

interface NominatimResult {
    display_name: string;
    lat: string;
    lon: string;
}

const parseLocation = (result: NominatimResult): LocationOption => {
    return {
        address: result.display_name,
        lat: Number(result.lat),
        lng: Number(result.lon)
    };
}

const ensureHttpSuccess = async (response: Response) => {
    if (response.ok) {
        return response;
    }

    const text = await response.text();
    throw new Error(text || "Location lookup failed.");
}

const search = async (
    query: string,
    options?: { limit?: number; signal?: AbortSignal }
) => {
    const trimmed = (query || "").trim();
    if (!trimmed) {
        return [] as LocationOption[];
    }

    const limit = Math.max(1, Math.min(options?.limit || 5, 10));
    const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=${limit}&q=${encodeURIComponent(trimmed)}`,
        {
            method: "GET",
            signal: options?.signal
        }
    );

    const safeResponse = await ensureHttpSuccess(response);
    const results = await safeResponse.json() as NominatimResult[];

    return results
        .map((result) => parseLocation(result))
        .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
}

const reverse = async (
    lat: number,
    lng: number,
    options?: { signal?: AbortSignal }
) => {
    const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`,
        {
            method: "GET",
            signal: options?.signal
        }
    );

    const safeResponse = await ensureHttpSuccess(response);
    const result = await safeResponse.json() as NominatimResult;
    const parsed = parseLocation(result);

    if (!parsed.address) {
        throw new Error("No address found for selected location.");
    }

    return parsed;
}

const LocationService = {
    search,
    reverse
}

export default LocationService;
